import React, { lazy, Suspense } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';
import { useAuth } from './context/AuthContext';

// Layout
import Header from './components/Header';
import Footer from './components/Footer';
import TrackingPixels from './components/TrackingPixels';
import TrackingConsent from './components/TrackingConsent';
import ScrollToTop from './components/ScrollToTop';
import Loading from './components/Loading';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { loadEventPage } from './utils/eventPageLoader';
import MaintenanceBanner from './components/MaintenanceNotice';
import ClientViewAsBoundary from './components/ClientViewAsBoundary';
import FinanceViewAsRouteGuard from './components/FinanceViewAsRouteGuard';
import CheckoutEntry from './components/CheckoutEntry';

// Pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const EventListing = lazy(() => import('./pages/EventListing'));
const EventDetails = lazy(loadEventPage);
const Checkout = lazy(() => import('./pages/Checkout'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderDetails = lazy(() => import('./pages/OrderDetails'));
const Favourites = lazy(() => import('./pages/Favourites'));
const PaymentPlans = lazy(() => import('./pages/PaymentPlans'));
const BrandPage = lazy(() => import('./pages/BrandPage'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminEvents = lazy(() => import('./pages/admin/Events'));
const AdminEventForm = lazy(() => import('./pages/admin/EventForm'));
const AdminOrders = lazy(() => import('./pages/admin/Orders'));
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics'));
const AdminBrands = lazy(() => import('./pages/admin/Brands'));
const AdminBrandForm = lazy(() => import('./pages/admin/BrandForm'));
const AdminBrandStats = lazy(() => import('./pages/admin/BrandStats'));
const AdminEmbedCheckout = lazy(() => import('./pages/admin/EmbedCheckout'));
const AdminEventCancellation = lazy(() => import('./pages/admin/EventCancellation'));
const AdminReports = lazy(() => import('./pages/admin/Reports'));
const AdminReportForm = lazy(() => import('./pages/admin/ReportForm'));
const AdminPromoCodes = lazy(() => import('./pages/admin/PromoCodes'));
const AdminPromoCodeForm = lazy(() => import('./pages/admin/PromoCodeForm'));
const AdminOrderRefund = lazy(() => import('./pages/admin/OrderRefund'));
const AdminWaitlist = lazy(() => import('./pages/admin/Waitlist'));
const AdminGuestList = lazy(() => import('./pages/admin/GuestList'));
const AdminEmailAttendees = lazy(() => import('./pages/admin/EmailAttendees'));
const AdminReferrals = lazy(() => import('./pages/admin/Referrals'));
const AdminBoxOffice = lazy(() => import('./pages/admin/BoxOffice'));
const AdminAddAttendee = lazy(() => import('./pages/admin/AddAttendee'));
const CheckIn = lazy(() => import('./pages/admin/CheckIn'));
const AdminScanners = lazy(() => import('./pages/admin/Scanners'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminOrganizerApplications = lazy(() => import('./pages/admin/OrganizerApplications'));
const AdminAbandonedCarts = lazy(() => import('./pages/admin/AbandonedCarts'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminReserves = lazy(() => import('./pages/admin/Reserves'));
const AdminFinance = lazy(() => import('./pages/admin/Finance'));
const AdminFinanceDetail = lazy(() => import('./pages/admin/FinanceDetail'));
const AdminClientViewAs = lazy(() => import('./pages/admin/ClientViewAs'));
const AdminFinanceViewAs = lazy(() => import('./pages/admin/FinanceViewAs'));
const OrganiserFinance = lazy(() => import('./pages/OrganiserFinance'));
const OrganizerWelcome = lazy(() => import('./pages/admin/OrganizerWelcome'));
const NotFound = lazy(() => import('./pages/NotFound'));
const FAQ = lazy(() => import('./pages/FAQ'));
const BecomeOrganiser = lazy(() => import('./pages/BecomeOrganiser'));
const EmbeddedTicketing = lazy(() => import('./pages/EmbeddedTicketing'));
const PaymentPlanFeatures = lazy(() => import('./pages/PaymentPlanFeatures'));
const ResaleSuccess = lazy(() => import('./pages/ResaleSuccess'));
const ScannerDashboard = lazy(() => import('./pages/scanner/ScannerDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const WaitlistStatus = lazy(() => import('./pages/WaitlistStatus'));
const WaitlistLeave = lazy(() => import('./pages/WaitlistLeave'));
const Contact = lazy(() => import('./pages/Contact'));
const FindTickets = lazy(() => import('./pages/FindTickets'));

// Protected Route
import ProtectedRoute from './components/ProtectedRoute';

function App() {
  const { viewAs } = useAuth();
  const isNativeScannerApp = Capacitor.isNativePlatform();

  if (isNativeScannerApp) {
    return (
      <div className="app scanner-native-app">
        <ScrollToTop />
        <main className="main-content">
          <Suspense fallback={<Loading page text="Loading page..." />}>
            <Routes>
              <Route path="*" element={<ScannerDashboard />} />
            </Routes>
          </Suspense>
        </main>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000
          }}
        />
      </div>
    );
  }

  return (
    <ClientViewAsBoundary><FinanceViewAsRouteGuard>
    <div className="app">
      <ScrollToTop />
      {!viewAs && <TrackingPixels />}
      {!viewAs && <TrackingConsent />}
      <Header />
      <MaintenanceBanner />
      <main className="main-content">
        <RouteErrorBoundary><Suspense fallback={<Loading page text="Loading page..." />}>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/events" element={<EventListing />} />
          <Route path="/events/:slug" element={<EventDetails />} />
          <Route path="/events/:slug/*" element={<EventDetails />} />
          <Route path="/checkout" element={<CheckoutEntry><Checkout /></CheckoutEntry>} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/resume/:token" element={<Checkout />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/find-tickets" element={<FindTickets />} />
          <Route path="/brands/:slug" element={<BrandPage />} />
          <Route path="/waitlist/status/:token" element={<WaitlistStatus />} />
          <Route path="/waitlist/leave/:token" element={<WaitlistLeave />} />
          <Route path="/become-organiser" element={<BecomeOrganiser />} />
          <Route path="/embedded-ticketing" element={<EmbeddedTicketing />} />
          <Route path="/payment-plans-for-events" element={<PaymentPlanFeatures />} />
          <Route path="/scanner" element={<ScannerDashboard />} />
          <Route path="/resale/success" element={
            <ProtectedRoute>
              <ResaleSuccess />
            </ProtectedRoute>
          } />

          {/* Protected Routes */}
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/orders" element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          } />
          <Route path="/orders/:id" element={
            <ProtectedRoute>
              <OrderDetails />
            </ProtectedRoute>
          } />
          <Route path="/favourites" element={
            <ProtectedRoute>
              <Favourites />
            </ProtectedRoute>
          } />
          <Route path="/payment-plans" element={
            <ProtectedRoute>
              <PaymentPlans />
            </ProtectedRoute>
          } />
          <Route path="/payment-plans/:id" element={<PaymentPlans />} />
          <Route path="/reinstate/:id" element={
            <ProtectedRoute>
              <PaymentPlans reinstatement />
            </ProtectedRoute>
          } />
          <Route path="/reinstate/:id/:token" element={
            <ProtectedRoute>
              <PaymentPlans reinstatement />
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin/view-as" element={<ProtectedRoute requiredRole="admin"><AdminClientViewAs /></ProtectedRoute>} />
          <Route path="/admin" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/welcome" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <OrganizerWelcome />
            </ProtectedRoute>
          } />
          <Route path="/admin/events" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminEvents />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/new" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminEventForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:id/edit" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminEventForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:id/analytics" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:id/checkin" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <CheckIn />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:id/scanners" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminScanners />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:id/embed" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminEmbedCheckout />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:id/cancel" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminEventCancellation />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:eventId/waitlist" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminWaitlist />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:eventId/guest-list" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminGuestList />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:eventId/email" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminEmailAttendees />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:id/referrals" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminReferrals />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:id/box-office" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminBoxOffice />
            </ProtectedRoute>
          } />
          <Route path="/admin/events/:id/add-attendee" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminAddAttendee />
            </ProtectedRoute>
          } />
          <Route path="/admin/orders" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminOrders />
            </ProtectedRoute>
          } />
          <Route path="/admin/orders/:orderId/manage" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminOrderRefund />
            </ProtectedRoute>
          } />
          <Route path="/admin/brands" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminBrands />
            </ProtectedRoute>
          } />
          <Route path="/admin/brands/new" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminBrandForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/brands/:id/edit" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminBrandForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/brands/:id/stats" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminBrandStats />
            </ProtectedRoute>
          } />
          <Route path="/admin/reports" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminReports />
            </ProtectedRoute>
          } />
          <Route path="/admin/reports/new" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminReportForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/reports/:id/edit" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminReportForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/promo-codes" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminPromoCodes />
            </ProtectedRoute>
          } />
          <Route path="/admin/promo-codes/new" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminPromoCodeForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/promo-codes/:id/edit" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <AdminPromoCodeForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute requiredRole="admin">
              <AdminSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/organizer-applications" element={
            <ProtectedRoute requiredRole="admin">
              <AdminOrganizerApplications />
            </ProtectedRoute>
          } />
          <Route path="/admin/carts" element={
            <ProtectedRoute requiredRole="admin">
              <AdminAbandonedCarts />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute requiredRole="admin">
              <AdminUsers />
            </ProtectedRoute>
          } />
          <Route path="/admin/reserves" element={
            <ProtectedRoute requiredRole="admin">
              <AdminReserves />
            </ProtectedRoute>
          } />
          <Route path="/admin/payouts" element={
            <ProtectedRoute requiredRole="admin">
              <Navigate to="/admin/finance" replace />
            </ProtectedRoute>
          } />
          <Route path="/admin/finance" element={
            <ProtectedRoute requiredRole="admin">
              <AdminFinance />
            </ProtectedRoute>
          } />
          <Route path="/admin/finance/view-as" element={
            <ProtectedRoute requiredRole="admin">
              <AdminFinanceViewAs />
            </ProtectedRoute>
          } />
          <Route path="/admin/finance/:brandId" element={
            <ProtectedRoute requiredRole="admin">
              <AdminFinanceDetail />
            </ProtectedRoute>
          } />
          <Route path="/organiser/finance" element={
            <ProtectedRoute requiredRole={['admin', 'organizer']}>
              <OrganiserFinance />
            </ProtectedRoute>
          } />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense></RouteErrorBoundary>
      </main>
      <Footer />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid #333'
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff'
            }
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff'
            }
          }
        }}
      />
    </div>
    </FinanceViewAsRouteGuard></ClientViewAsBoundary>
  );
}

export default App;
